"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  Panel,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseScientificInput } from "@/lib/tool-kit/earth-science/format";
import {
  absoluteHumidity,
  dewPoint,
  frostPoint,
  MAGNUS_WATER_RANGE_C,
  saturationVaporPressure,
  vaporPressure,
} from "@/lib/tool-kit/earth-science/humidity";
import {
  differenceFromCelsius,
  fromCelsius,
  type TemperatureUnit,
  toCelsius,
  unitSymbol,
} from "@/lib/tool-kit/earth-science/temperature";

/** English strings. A translated content file overrides these through `ui`. */
export const DEW_POINT_CALCULATOR_STRINGS = {
  temperatureLabel: "Air temperature",
  unitLabel: "Temperature unit",
  unitC: "Celsius (°C)",
  unitF: "Fahrenheit (°F)",
  unitK: "Kelvin (K)",
  humidityLabel: "Relative humidity (%)",
  humidityHint: "0–100, measured against liquid water as weather reports do.",
  results: "Results",
  dewPoint: "Dew point",
  frostPoint: "Frost point",
  frostPointNone: "Not applicable",
  spread: "Dew point depression (temperature − dew point)",
  vaporPressure: "Vapor pressure e (hPa)",
  saturationPressure: "Saturation vapor pressure (hPa)",
  absoluteHumidity: "Absolute humidity (g/m³)",
  comfortLabel: "How it feels",
  comfortDry: "Dry and comfortable",
  comfortSticky: "Getting sticky and muggy",
  comfortOppressive: "Oppressive",
  comfortNote:
    "Comfort bands follow the rule of thumb many National Weather Service offices quote: a dew point of 55 °F (13 °C) or below feels dry, 55–65 °F (13–18 °C) feels sticky, and 65 °F (18 °C) or above feels oppressive.",
  workingHeading: "The working",
  stepSaturation:
    "Saturation vapor pressure at t = {t} °C: es = 6.1094 × exp(17.625 t / (243.04 + t)) = {es} hPa.",
  stepVapor: "Vapor pressure: e = {rh} % × {es} hPa = {e} hPa.",
  stepDew:
    "Dew point: Td = 243.04 × ln(e / 6.1094) / (17.625 − ln(e / 6.1094)) = {td} °C.",
  stepFrost:
    "Frost point (ice constants 6.1121, 22.587, 273.86): Tf = {tf} °C.",
  tableHeading: "Dew point at {temperature} for other humidities",
  columnHumidity: "Relative humidity",
  columnDewPoint: "Dew point",
  columnSpread: "Depression",
  formulaNote:
    "Magnus formula with the Alduchov and Eskridge (1996) coefficients, fitted for −40 to 60 °C over water. The error in saturation vapor pressure is about 0.4 %, which moves the dew point by less than 0.1 °C.",
  outOfRange:
    "The Magnus coefficients are fitted for −40 to 60 °C; at {temperature} the result is a rougher estimate.",
  supersaturated:
    "Relative humidity above 100 % means supersaturated air, which is brief and rare; the dew point will be above the air temperature.",
  copyResults: "Copy results",
  copied: "Copied!",
  copyLine:
    "At {temperature} and {rh} % relative humidity: dew point {dew}, vapor pressure {e} hPa",
  enterValues: "Enter a temperature and a relative humidity to see the result.",
  humidityRange:
    "Relative humidity must be greater than 0 %; at 0 % there is no water vapor and no dew point.",
  temperatureRange:
    "That temperature is below absolute zero in the chosen unit. Check the number and the unit.",
} as const;

type Strings = typeof DEW_POINT_CALCULATOR_STRINGS;
type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

const TABLE_HUMIDITIES = [100, 90, 80, 70, 60, 50, 40, 30, 20, 10];
const ONE = { maximumFractionDigits: 1 };

/** A difference with floating-point noise (−0, 1e-15) cleaned to a plain zero. */
function spreadOf(celsiusDifference: number, unit: TemperatureUnit): number {
  const value = differenceFromCelsius(celsiusDifference, unit);
  return Math.abs(value) < 1e-9 ? 0 : value;
}

function degrees(
  fmt: Fmt,
  celsius: number,
  unit: TemperatureUnit,
  digits = 1
): string {
  return `${fmt(fromCelsius(celsius, unit), { maximumFractionDigits: digits })} ${unitSymbol(unit)}`;
}

function comfort(strings: Strings, dewPointCelsius: number): string {
  const fahrenheit = fromCelsius(dewPointCelsius, "F");
  if (fahrenheit >= 65) {
    return strings.comfortOppressive;
  }
  return fahrenheit > 55 ? strings.comfortSticky : strings.comfortDry;
}

function workingSteps(
  strings: Strings,
  fmt: Fmt,
  celsius: number,
  humidity: number,
  frost: number | null
): string[] {
  const es = saturationVaporPressure(celsius);
  const e = vaporPressure(celsius, humidity);
  const steps = [
    t(strings.stepSaturation, {
      t: fmt(celsius, { maximumFractionDigits: 2 }),
      es: fmt(es, { maximumFractionDigits: 3 }),
    }),
    t(strings.stepVapor, {
      rh: fmt(humidity, { maximumFractionDigits: 2 }),
      es: fmt(es, { maximumFractionDigits: 3 }),
      e: fmt(e, { maximumFractionDigits: 3 }),
    }),
    t(strings.stepDew, {
      td: fmt(dewPoint(celsius, humidity), { maximumFractionDigits: 2 }),
    }),
  ];
  if (frost !== null) {
    steps.push(
      t(strings.stepFrost, { tf: fmt(frost, { maximumFractionDigits: 2 }) })
    );
  }
  return steps;
}

function Results({
  celsius,
  humidity,
  unit,
  strings,
  fmt,
}: {
  celsius: number;
  humidity: number;
  unit: TemperatureUnit;
  strings: Strings;
  fmt: Fmt;
}) {
  const dew = dewPoint(celsius, humidity);
  const frostRaw = frostPoint(celsius, humidity);
  const frost = frostRaw <= 0 ? frostRaw : null;
  const e = vaporPressure(celsius, humidity);
  const outside =
    celsius < MAGNUS_WATER_RANGE_C.min || celsius > MAGNUS_WATER_RANGE_C.max;
  const temperatureText = degrees(fmt, celsius, unit);
  return (
    <>
      {outside ? (
        <Notice>
          {t(strings.outOfRange, { temperature: temperatureText })}
        </Notice>
      ) : null}
      {humidity > 100 ? <Notice>{strings.supersaturated}</Notice> : null}
      <StatGrid>
        <StatCard
          emphasis
          label={strings.dewPoint}
          value={degrees(fmt, dew, unit)}
        />
        <StatCard
          label={strings.frostPoint}
          value={
            frost === null ? strings.frostPointNone : degrees(fmt, frost, unit)
          }
        />
        <StatCard label={strings.comfortLabel} value={comfort(strings, dew)} />
        <StatCard
          label={strings.spread}
          value={`${fmt(spreadOf(celsius - dew, unit), ONE)} ${unitSymbol(unit)}`}
        />
        <StatCard
          label={strings.vaporPressure}
          value={fmt(e, { maximumFractionDigits: 2 })}
        />
        <StatCard
          label={strings.absoluteHumidity}
          value={fmt(absoluteHumidity(e, celsius), {
            maximumFractionDigits: 1,
          })}
        />
      </StatGrid>
      <p className={HINT}>{strings.comfortNote}</p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResults}
        small
        text={t(strings.copyLine, {
          temperature: temperatureText,
          rh: fmt(humidity, { maximumFractionDigits: 2 }),
          dew: degrees(fmt, dew, unit),
          e: fmt(e, { maximumFractionDigits: 2 }),
        })}
      />
      <Panel title={strings.workingHeading}>
        <ol className="list-decimal space-y-1 pl-5 text-foreground text-sm">
          {workingSteps(strings, fmt, celsius, humidity, frost).map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </Panel>
      <h3 className={WIDGET_H3}>
        {t(strings.tableHeading, { temperature: temperatureText })}
      </h3>
      <DataTable
        head={[
          strings.columnHumidity,
          strings.columnDewPoint,
          strings.columnSpread,
        ]}
        rows={TABLE_HUMIDITIES.map((rh) => {
          const td = dewPoint(celsius, rh);
          return [
            `${fmt(rh)} %`,
            degrees(fmt, td, unit),
            `${fmt(spreadOf(celsius - td, unit), ONE)}`,
          ];
        })}
      />
      <p className={HINT}>{strings.formulaNote}</p>
    </>
  );
}

function Outcome({
  temperature,
  humidity,
  unit,
  strings,
  fmt,
}: {
  temperature: string;
  humidity: string;
  unit: TemperatureUnit;
  strings: Strings;
  fmt: Fmt;
}) {
  const typed = parseScientificInput(temperature);
  const rh = parseScientificInput(humidity);
  if (typed === null || rh === null) {
    return <Notice>{strings.enterValues}</Notice>;
  }
  const celsius = toCelsius(typed, unit);
  if (celsius < -273.15) {
    return <ErrorNotice>{strings.temperatureRange}</ErrorNotice>;
  }
  if (rh <= 0) {
    return <ErrorNotice>{strings.humidityRange}</ErrorNotice>;
  }
  return (
    <Results
      celsius={celsius}
      fmt={fmt}
      humidity={rh}
      strings={strings}
      unit={unit}
    />
  );
}

export default function DewPointCalculatorWidget() {
  const strings = useToolUi(DEW_POINT_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [temperature, setTemperature] = useState("25");
  const [unit, setUnit] = useState<TemperatureUnit>("C");
  const [humidity, setHumidity] = useState("60");

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          id="dpc-temperature"
          label={strings.temperatureLabel}
          numeric
          onChange={setTemperature}
          value={temperature}
        />
        <SelectField
          id="dpc-unit"
          label={strings.unitLabel}
          onChange={setUnit}
          options={[
            { value: "C", label: strings.unitC },
            { value: "F", label: strings.unitF },
            { value: "K", label: strings.unitK },
          ]}
          value={unit}
        />
        <TextField
          hint={strings.humidityHint}
          id="dpc-humidity"
          label={strings.humidityLabel}
          numeric
          onChange={setHumidity}
          value={humidity}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome
          fmt={fmt}
          humidity={humidity}
          strings={strings}
          temperature={temperature}
          unit={unit}
        />
      </section>
    </div>
  );
}
